import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, finalize, of } from 'rxjs';
import { CaseManagementService } from '../../../../../../../../services/case-management.service';
import { CaseAccessControlEventModel } from '@models/case-access-control-event.model';
import {
  CaseAccessControlEventInfoDialogComponent,
  CaseAccessControlEventInfoDialogData,
} from '../../../../../../../../pages/case-management/dialogs/case-access-control-event-info-dialog/case-access-control-event-info-dialog.component';
import { ThumbnailDialogType } from '../../../../../../../thumbnail/thumbnail-dialog/thumbnail-dialog.component';
import { PreloaderColor, UiPreloaderComponent } from '@ui';
import { UiSearchComponent } from '../../../../../../../ui-kit/ui-search/ui-search.component';

@Component({
  selector: 'app-case-manager-access-events-preview',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TranslatePipe,
    UiPreloaderComponent,
    UiSearchComponent,
  ],
  templateUrl: './case-manager-access-events-preview.component.html',
  styleUrls: ['../case-manager-feature-preview.shared.scss'],
})
export class CaseManagerAccessEventsPreviewComponent implements OnInit {
  private readonly caseManagementService = inject(CaseManagementService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  readonly caseId = input.required<string>();
  readonly currentUserId = input<string>('');

  protected readonly preloaderColor = PreloaderColor.blue;
  protected readonly events = signal<CaseAccessControlEventModel.CaseAccessControlEventDocument[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly searchTerm = signal('');
  protected readonly filteredEvents = computed(() => {
    const query = this.searchTerm().trim().toLowerCase();
    if (!query) {
      return this.events();
    }

    return this.events().filter((event) => [
      event.eventName,
      event.userName,
      event.device ?? '',
      event.site ?? '',
      event.lastComment ?? '',
    ].some((value) => value.toLowerCase().includes(query)));
  });

  public ngOnInit(): void {
    this.loadEvents();
  }

  protected openEvent(event: CaseAccessControlEventModel.CaseAccessControlEventDocument): void {
    const dialogData: CaseAccessControlEventInfoDialogData = {
      caseId: this.caseId(),
      caseAccessControlEventId: event._id,
      currentUserId: this.currentUserId(),
      canEdit: false,
      options: {
        startTime: event.timestamp,
        endTime: event.timestamp,
        clipInSeconds: 7200,
        offsetResInDurations: 60,
        duration: 2000,
      },
      events: null,
      edgeId: null,
      cameraId: null,
      objects: [],
      type: ThumbnailDialogType.ACCESS_CONTROL_EVENTS,
      rtl: false,
      defaultThumb: '',
      showObjects: false,
      seconds: false,
      allowTrack: false,
      isShareVisible: false,
      name: 'CONTROL_EVENTS.INFO.EVENT_DETAILS',
      isSharedAlert: false,
      controlEventOptions: {
        eventId: event.eventId,
        openInfoTab: true,
      },
    };

    this.dialog.open(CaseAccessControlEventInfoDialogComponent, {
      panelClass: 'thumbnail-dialog-wrapper',
      width: '110vh',
      maxHeight: '95vh',
      data: dialogData,
    });
  }

  private loadEvents(): void {
    this.isLoading.set(true);
    this.caseManagementService.getCaseAccessControlEvents({ caseId: this.caseId() })
      .pipe(
        catchError(() => of([])),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((events) => this.events.set(events));
  }
}
